import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../config/firebase";

export const useDarts = () => {
  const { t } = useTranslation();
  const { userInfo } = useSelector((state) => state.user);

  const [darts, setDarts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch darts from Firestore
  useEffect(() => {
    if (!userInfo?.uid) {
      setDarts([]);
      return;
    }

    setIsLoading(true);
    const colRef = collection(db, "userInfo", userInfo.uid, "darts");
    const q = query(colRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const dartData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDarts(dartData);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching darts:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userInfo]);

  // Add or update dart
  const saveDart = async (dartData, dartId = null) => {
    if (!userInfo?.uid) {
      throw new Error(t("settings.messages.pleaseLoginFirst"));
    }

    const payload = { ...dartData, timestamp: serverTimestamp() };

    try {
      if (dartId) {
        await updateDoc(
          doc(db, "userInfo", userInfo.uid, "darts", dartId),
          payload
        );
        return { success: true, message: t("settings.messages.dartUpdated") };
      } else {
        await addDoc(
          collection(db, "userInfo", userInfo.uid, "darts"),
          payload
        );
        return { success: true, message: t("settings.messages.dartAdded") };
      }
    } catch (error) {
      console.error("Error saving dart:", error);
      throw new Error(t("settings.messages.saveFailed"));
    }
  };

  // Delete dart
  const deleteDart = async (dartId) => {
    if (!userInfo?.uid) {
      throw new Error(t("settings.messages.pleaseLoginFirst"));
    }

    if (!window.confirm(t("settings.myDarts.confirmDelete"))) {
      return { success: false, cancelled: true };
    }

    try {
      await deleteDoc(doc(db, "userInfo", userInfo.uid, "darts", dartId));
      return { success: true, message: t("settings.messages.dartDeleted") };
    } catch (error) {
      console.error("Error deleting dart:", error);
      throw new Error(t("settings.messages.deleteFailed"));
    }
  };

  return {
    darts,
    isLoading,
    saveDart,
    deleteDart,
  };
};
